/* EditImageRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;

class EditImageRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("edit-image-veil-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        // Form fields
        this._nameField = new InputTextControl({
            darkTheme: true,
            label: "Name",
            placeholder: "Enter Image Name",
            value: this._model.name,
        });

        this._descriptionField = new InputTextControl({
            darkTheme: true,
            label: "Description",
            placeholder: "Enter Image Description (optional)",
            value: this._model.description,
        });

        this._urlField = new InputTextControl({
            darkTheme: true,
            label: "URL",
            placeholder: "Enter Image URL (starting with http:// or https://)",
            value: this._model.url,
        });

        // Error message container
        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("edit-image-error");
        this._errorContainer.style.display = "none";

        // Buttons
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
            theme: "text",
        });

        this._saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            theme: "additive",
        });

        this._buttonContainer = document.createElement("button-container");

        this._registerListeners();
        this._updateSaveButtonState();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
        this._saveButton.on(
            "UserClick",
            (this._onSaveButtonClick = this._onSaveButtonClick.bind(this))
        );
        this._nameField.on("UserValue", (this._onNameChange = this._onNameChange.bind(this)));
        this._descriptionField.on(
            "UserValue",
            (this._onDescriptionChange = this._onDescriptionChange.bind(this))
        );
        this._urlField.on("UserValue", (this._onUrlChange = this._onUrlChange.bind(this)));
        this._model.on(
            "ValidationError",
            (this._onValidationError = this._onValidationError.bind(this))
        );
        this._model.on("FormChanged", (this._onFormChanged = this._onFormChanged.bind(this)));
    }

    _unregisterListeners() {
        this._cancelButton.removeAllListeners("UserClick");
        this._saveButton.removeAllListeners("UserClick");
        this._nameField.removeAllListeners("UserValue");
        this._descriptionField.removeAllListeners("UserValue");
        this._urlField.removeAllListeners("UserValue");
        this._model.removeAllListeners("ValidationError");
        this._model.removeAllListeners("FormChanged");
    }

    _updateSaveButtonState() {
        // Save button is enabled only if form is valid AND entity is dirty
        const canSave = this._model.canSave();
        this._saveButton.setDisabled(!canSave);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        const promArr = [
            this._nameField.shutdown(),
            this._descriptionField.shutdown(),
            this._urlField.shutdown(),
            this._cancelButton.shutdown(),
            this._saveButton.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._nameField = undefined;
                this._descriptionField = undefined;
                this._urlField = undefined;
                this._cancelButton = undefined;
                this._saveButton = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                if (this._model && this._model.logger) {
                    this._model.logger.warn(err, "EditImageRenderer.shutdown() error");
                }
            }
        );
    }

    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                const createFieldRow = (labelText, field) => {
                    const row = document.createElement("field-row");
                    const labelElem = document.createElement("label");
                    labelElem.innerText = labelText + ":";
                    row.appendChild(labelElem);
                    row.appendChild(field.renderer.rootElement);
                    return row;
                };

                this._rootElem.appendChild(this._titleComponent);
                this._rootElem.appendChild(createFieldRow("Name", this._nameField));
                this._rootElem.appendChild(createFieldRow("Description", this._descriptionField));

                if (this.model._linkStatus !== "EXTERNAL") {
                    this._rootElem.appendChild(createFieldRow("URL", this._urlField));
                }

                this._rootElem.appendChild(this._buttonContainer);
                this._rootElem.appendChild(this._errorContainer);
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._saveButton.renderer.rootElement);

                setTimeout(() => {
                    this._nameField.focus();
                });
            }
            resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onCancelButtonClick() {
        this.model.cancelAction();
    }

    _onSaveButtonClick() {
        this._model.validateAndSave();
    }

    _onNameChange(value) {
        this._model.setName(value);
    }

    _onDescriptionChange(value) {
        this._model.setDescription(value);
    }

    _onUrlChange(value) {
        this._model.setUrl(value);
    }

    _onValidationError(error) {
        if (error) {
            this._errorContainer.innerText = error;
            this._errorContainer.style.display = "block";
        } else {
            this._errorContainer.innerText = "";
            this._errorContainer.style.display = "none";
        }
    }

    _onFormChanged() {
        this._updateSaveButtonState();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /*******************************************/
}

module.exports = EditImageRenderer;
